/**
 * A date-range filter value, as the list machinery holds it.
 *
 * Either bound may be absent: `parseFiltersFromUrl` deliberately accepts
 * `?x_from=` on its own, and writes both keys with the missing one undefined.
 */
type DateRangeValue = { startDate?: unknown; endDate?: unknown };

/** ISO-8601 for a bound the backend can use, or null for one it cannot. */
const toIsoBound = (value: unknown): string | null => {
  if (value === null || value === undefined || value === '') return null;
  const parsed = new Date(value as string | number | Date);
  // An unparseable bound is dropped rather than thrown. `toISOString` on an
  // Invalid Date raises RangeError, which the calling thunk catches as a
  // failure -- so one bad bound rendered "Invalid time value" and no rows at
  // all, where the rest of the filters would have worked perfectly well.
  return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString();
};

export const flattenQuery = (params: Record<string, any>) => {
  const out: Record<string, any> = {};
  Object.entries(params).forEach(([k, v]) => {
    if (v === null || v === undefined || v === '' || (Array.isArray(v) && !v.length)) return;

    // Either key marks a date range. Matching on both would miss a one-sided
    // range built in code rather than parsed from the URL.
    if (typeof v === 'object' && !Array.isArray(v) && ('startDate' in v || 'endDate' in v)) {
      // Emitted per bound, so one absent bound leaves the other applied. The
      // backend filters on whichever it is given.
      const from = toIsoBound((v as DateRangeValue).startDate);
      const to = toIsoBound((v as DateRangeValue).endDate);
      if (from !== null) out[`${k}_from`] = from;
      if (to !== null) out[`${k}_to`] = to;
    } else {
      out[k] = Array.isArray(v) ? v.join(',') : v;
    }
  });
  return out;
};
